import {
  Grid,
  GridProps,
  GridSize,
  Typography,
  Tooltip,
  TypographyProps,
} from "@mui/material";
import type { SystemProps } from "@mui/system";
import type { Theme } from "@mui/material/styles";
import { DEFAULT_FONTSIZE, replaceUnderScoreAddSpace } from "@/utils/utils";
import { mergeSx, splitSystemProps } from "@/utils/system-props";
import { ReactNode } from "react";

type ProfileViewerProps = {
  data: { [k: string]: string | object };
  w?: string | number;
  firstColW?: GridSize;
  secondColW?: GridSize;
  withoutColn?: boolean;
} & GridProps &
  SystemProps<Theme>;

const OVERFLOW_WRAP = { overflowWrap: "break-word" };

/**
 * @param w `width`
 * @param {object} data
 * @warning  `data must be a object`
 *
 * @returns profile view
 *
 * @author rajkumar rathod
 */
const ProfileViewer = ({
  w = "100%",
  data,
  firstColW = 2.5,
  secondColW = 5.5,
  withoutColn = true,
  sx,
  ...others
}: ProfileViewerProps) => {
  const { systemSx, rest } = splitSystemProps(others);
  return (
    <Grid
      container
      {...rest}
      sx={mergeSx({ gap: "0.65rem", width: w, ...systemSx }, sx)}
    >
      {Object.entries(data).map(([k, value]) => (
        <Grid key={k} container size={12}>
          <Grid size={firstColW ?? 4}>
            <Typography
              sx={{
                fontWeight: "medium",
                color: "#1A1A1A80",
                fontSize: DEFAULT_FONTSIZE,
              }}
            >
              {replaceUnderScoreAddSpace(k)}
            </Typography>
          </Grid>
          {withoutColn && (
            <Grid
              sx={{
                padding: 0,
                paddingLeft: "5px",
                fontSize: DEFAULT_FONTSIZE.fontSize,
              }}
              size="auto"
            >
              :
            </Grid>
          )}
          <Grid
            container
            sx={{
              gap: systemSx.gap,
              paddingLeft: "0.6rem",
              display: "inline-block",
            }}
            size={secondColW ? (secondColW as any) - (withoutColn ? 1 : 0) : 8}
          >
            {/* 
               checking for value first so that it consider as not nullable and undefined value
               strick check the type as object and loop over the object to show the data 
               for nested data it shows the list view 

               if there is long data we can actually trimmed data with tooltip 
               - add true as key
               - add label that you need to render
               - add trimmed value after how many char you wanna trimmed the string

               for ex - 
                physical_address: {
                  true: {
                    label:"lane1 lane 2 ",
                    trimmed: 30
                  }
            */}
            {value &&
              typeof value === "object" &&
              Object.entries(data[k]).map(([k, value]) => {
                if (typeof value === "string")
                  return <StyledText>{value ?? "-"}</StyledText>;

                /** CSS ellipsis truncation - uses available space */
                if (k === "true" && value?.useEllipsis) {
                  return (
                    <StyledText>
                      <Tooltip title={value?.label || ""} placement="top">
                        <div
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            width: "100%",
                            maxWidth: "100%",
                          }}
                        >
                          {value?.label ?? "-"}
                        </div>
                      </Tooltip>
                    </StyledText>
                  );
                }

                if (k === "custom") {
                  return value();
                }

                /** trimmed value */
                // if (k === "true" && value?.label?.length >= value?.trimmed) {
                //   return (
                //     <StyledText>
                //       <Tooltip title={value.label ?? ""} placement="top">
                //         <div>
                //           : {value?.label?.slice(0, value.trimmed) + "..."}
                //         </div>
                //       </Tooltip>
                //     </StyledText>
                //   );
                // }

                return (
                  <StyledText style={value?.style}>
                    {value.label ?? "-"}
                  </StyledText>
                );
              })}

            {typeof value === "string" && (
              <StyledText>{value ?? "-"}</StyledText>
            )}

            {!value && ""}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};

export default ProfileViewer;

export const StyledText = ({
  children,
  ...others
}: { children: ReactNode } & TypographyProps) => (
  <Typography
    {...others}
    sx={[
      {
        fontSize: DEFAULT_FONTSIZE,
      },
      ...(Array.isArray(OVERFLOW_WRAP) ? OVERFLOW_WRAP : [OVERFLOW_WRAP]),
      others.sx,
    ]}
  >
    {children}
  </Typography>
);
